import * as THREE from 'three';
import { SURFACE_CELL_SIZE } from '../../spatial/surfaces';
import type { FloorStyleId } from '../../types';
import { bakeColored, mergeBufferGeometries, planarUv, tintGeometry } from './batch';
import { discGeometry, makeShadowBlobTexture, mesh, padGeometry, rod } from './helpers';
import type { ParkMaterials } from './materials';

export type PathPiece =
  | { kind: 'rect'; x: number; z: number; w: number; d: number; radius?: number; rotY?: number }
  | { kind: 'disc'; x: number; z: number; r: number };

const PATH_BORDER = 0.14;

/** Raised turf island with a darker turf lip, soil band and bedrock, floating over a soft contact shadow. */
export function createIsland(
  mats: ParkMaterials,
  width: number,
  depth: number,
): { group: THREE.Group; parkTop: number } {
  const group = new THREE.Group();
  const grassHeight = 0.42;
  const soilHeight = 0.34;
  const rockHeight = 0.26;

  const grassGeometry = padGeometry(width, depth, grassHeight, 1.6, 0.16);
  planarUv(grassGeometry, 3.2);
  const grass = new THREE.Mesh(grassGeometry, [mats.grassTop, mats.grassSide]);
  grass.receiveShadow = true;
  grass.castShadow = false;
  grass.position.y = soilHeight + rockHeight - 0.02;
  group.add(grass);

  const soil = mesh(padGeometry(width - 0.5, depth - 0.5, soilHeight, 1.45, 0.05), mats.soil, {
    position: [0, rockHeight - 0.01, 0],
    cast: false,
    receive: false,
  });
  const rock = mesh(padGeometry(width - 0.95, depth - 0.95, rockHeight, 1.3, 0.05), mats.bedrock, {
    position: [0, 0, 0],
    cast: false,
    receive: false,
  });
  group.add(soil, rock);

  const shadow = new THREE.Mesh(
    new THREE.PlaneGeometry(width * 1.9, depth * 2.1),
    new THREE.MeshBasicMaterial({
      map: makeShadowBlobTexture(),
      transparent: true,
      depthWrite: false,
    }),
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.set(0.9, -0.55, 0.7);
  group.add(shadow);

  grassGeometry.computeBoundingBox();
  const parkTop = grass.position.y + (grassGeometry.boundingBox?.max.y ?? grassHeight);
  return { group, parkTop };
}

function pieceGeometry(piece: PathPiece, inset: number, height: number): THREE.BufferGeometry {
  if (piece.kind === 'disc') {
    const geometry = discGeometry(Math.max(piece.r - inset, 0.1), height, 48);
    geometry.translate(piece.x, 0, piece.z);
    return geometry;
  }
  const w = Math.max(piece.w - inset * 2, 0.2);
  const d = Math.max(piece.d - inset * 2, 0.2);
  const geometry = padGeometry(w, d, height, Math.max((piece.radius ?? 0.45) - inset, 0.05), 0.018);
  if (piece.rotY) geometry.rotateY(piece.rotY);
  geometry.translate(piece.x, 0, piece.z);
  return geometry;
}

/** Two merged layers: a darker edge band under a lighter paved fill, so overlapping pieces read as one path. */
export function createPathNetwork(mats: ParkMaterials, pieces: PathPiece[], parkTop: number): THREE.Group {
  const group = new THREE.Group();
  const edges = pieces.map((piece) => pieceGeometry(piece, 0, 0.05));
  const fills = pieces.map((piece) => pieceGeometry(piece, PATH_BORDER, 0.065));

  const edgeMesh = new THREE.Mesh(mergeBufferGeometries(edges), mats.pathEdge);
  edgeMesh.position.y = parkTop - 0.005;
  edgeMesh.receiveShadow = true;
  edgeMesh.castShadow = false;

  const fillGeometry = planarUv(mergeBufferGeometries(fills), 2.0);
  const fillMesh = new THREE.Mesh(fillGeometry, mats.path);
  fillMesh.position.y = parkTop - 0.002;
  fillMesh.receiveShadow = true;
  fillMesh.castShadow = false;

  edges.forEach((geometry) => geometry.dispose());
  fills.forEach((geometry) => geometry.dispose());
  group.add(edgeMesh, fillMesh);
  return group;
}

/** Hairline grout between paving tiles. Overlapping grout hides grass between neighbors. */
export const PATH_FLOOR_TILE_INSET = 0.012;
export const PATH_FLOOR_GROUT_OVERLAP = 0.02;
export const PATH_FLOOR_GROUT_HEIGHT = 0.02;
export const PATH_FLOOR_FILL_HEIGHT = 0.028;
/** Instanced fill mesh Y relative to parkTop for default corridor paving. */
export const PATH_FLOOR_FILL_Y_OFFSET = -0.002;
export const PATH_FLOOR_GROUT_Y_OFFSET = -0.006;
/**
 * Contextual lawn pad height + bevel. Custom painted cells must sit above this
 * so they replace/cover the lawn instead of rendering underneath it.
 */
export const CONTEXTUAL_LAWN_PAD_HEIGHT = 0.09;
export const CONTEXTUAL_LAWN_PAD_BEVEL = 0.01;
export const CONTEXTUAL_LAWN_VISUAL_HEIGHT = CONTEXTUAL_LAWN_PAD_HEIGHT + CONTEXTUAL_LAWN_PAD_BEVEL;
/** Extra lift so custom paving clears the lawn pad top with a visible step. */
export const PATH_FLOOR_OVER_LAWN_LIFT = CONTEXTUAL_LAWN_VISUAL_HEIGHT + 0.008;
/** One shared plane for default corridor and painted floors so seams cannot reveal lawn. */
export const PATH_FLOOR_SHARED_LIFT = 0.006;

export function contextualLawnTop(parkTop: number): number {
  return parkTop + CONTEXTUAL_LAWN_VISUAL_HEIGHT;
}

export function pathFloorFillWorldY(parkTop: number, lift = 0): number {
  return parkTop + PATH_FLOOR_FILL_Y_OFFSET + lift;
}

export function pathFloorFillTop(parkTop: number, lift = 0): number {
  return pathFloorFillWorldY(parkTop, lift) + PATH_FLOOR_FILL_HEIGHT;
}

export function pathFloorCellLift(_coversLawn: boolean): number {
  return PATH_FLOOR_SHARED_LIFT;
}

/**
 * Instanced 0.48 paving tiles using the existing path/pathEdge family.
 * Flat boxes, not raised lawn pads, so seams read as paving joints.
 */
type PathFloorMats = Pick<ParkMaterials, 'path' | 'pathEdge'> &
  Partial<
    Pick<
      ParkMaterials,
      | 'wood'
      | 'stone'
      | 'stoneDark'
      | 'pavingTile'
      | 'brickTile'
      | 'brickGrout'
      | 'cobbleTile'
      | 'cobbleGrout'
      | 'kidsTile'
      | 'kidsGrout'
      | 'woodTile'
      | 'woodGrout'
      | 'festiveTile'
      | 'festiveGrout'
      | 'flowerPinkTile'
      | 'flowerYellowTile'
      | 'flowerMixTile'
      | 'cloverTile'
      | 'gardenTile'
      | 'floralGrout'
      | 'gardenGrout'
      | 'baked'
    >
  >;

export type PathFloorStyle = Exclude<FloorStyleId, 'grass'>;

/**
 * Fable floor family (COL-011B). Each style is a tile-scale texture plus a matching grout
 * swatch. Plain swatches remain as fallbacks for material sets that do not carry the
 * textured tiles (tests, dummies).
 */
function materialsForStyle(mats: PathFloorMats, style: PathFloorStyle | undefined) {
  const pick = (fill: THREE.Material | undefined, edge: THREE.Material | undefined) =>
    fill ? { fill, edge: edge ?? mats.pathEdge } : null;
  switch (style) {
    case 'brick':
      return pick(mats.brickTile ?? mats.wood, mats.brickGrout) ?? classicFloor(mats);
    case 'stone':
      return pick(mats.cobbleTile ?? mats.stone, mats.cobbleGrout ?? mats.stoneDark) ?? classicFloor(mats);
    case 'kidsTile':
      return pick(mats.kidsTile, mats.kidsGrout) ?? classicFloor(mats);
    case 'wood':
      return pick(mats.woodTile ?? mats.wood, mats.woodGrout) ?? classicFloor(mats);
    case 'festive':
      return pick(mats.festiveTile, mats.festiveGrout) ?? classicFloor(mats);
    case 'flowerPink':
      return pick(mats.flowerPinkTile, mats.floralGrout) ?? classicFloor(mats);
    case 'flowerYellow':
      return pick(mats.flowerYellowTile, mats.floralGrout) ?? classicFloor(mats);
    case 'flowerMix':
      return pick(mats.flowerMixTile, mats.floralGrout) ?? classicFloor(mats);
    case 'clover':
      return pick(mats.cloverTile, mats.floralGrout) ?? classicFloor(mats);
    case 'garden':
      return pick(mats.gardenTile, mats.gardenGrout) ?? classicFloor(mats);
    default:
      return classicFloor(mats);
  }
}

function classicFloor(mats: PathFloorMats) {
  return { fill: mats.pavingTile ?? mats.path, edge: mats.pathEdge };
}

/** Raised bloom clusters per floral tile so the flower beds have a little 3D fluff. */
const FLORAL_BLOOM_PALETTES: Partial<Record<PathFloorStyle, number[]>> = {
  flowerPink: [0xff7fae, 0xff9ec6, 0xf75f97, 0xffb3cf],
  flowerYellow: [0xffe066, 0xffd23f, 0xffb833, 0xfff0a0],
  flowerMix: [0xff86b6, 0x8fd0ff, 0xc79bff, 0xffe066, 0xffffff],
  clover: [0xffffff, 0xfff4c2],
  garden: [0xe04a5a, 0xffd97a],
};

const FLORAL_BLOOM_OFFSETS: Array<[number, number]> = [
  [-0.14, -0.1],
  [0.13, -0.14],
  [0.02, 0.12],
  [-0.12, 0.15],
];

function floralBloomGeometry(style: PathFloorStyle): THREE.BufferGeometry | null {
  const palette = FLORAL_BLOOM_PALETTES[style];
  if (!palette) {
    return null;
  }
  const parts: THREE.BufferGeometry[] = [];
  const offsets = style === 'garden' ? [[0, 0] as [number, number]] : FLORAL_BLOOM_OFFSETS;
  offsets.forEach(([ox, oz], index) => {
    const radius = style === 'garden' ? 0.05 : style === 'clover' ? 0.022 : 0.036;
    const bloom = new THREE.SphereGeometry(radius, 6, 4);
    bloom.translate(ox, radius * 0.9, oz);
    parts.push(tintGeometry(bloom, palette[index % palette.length]!));
    if (style !== 'clover') {
      const leaf = new THREE.SphereGeometry(radius * 0.8, 5, 3);
      leaf.scale(1.3, 0.45, 1);
      leaf.translate(ox + radius * 0.6, radius * 0.35, oz + radius * 0.4);
      parts.push(tintGeometry(leaf, 0x4f9a3a));
    }
  });
  const merged = mergeBufferGeometries(parts);
  parts.forEach((part) => part.dispose());
  return merged;
}

export function createPathFloorPresentation(
  mats: PathFloorMats,
  cells: Array<{ x: number; z: number; style?: PathFloorStyle; lift?: number }>,
  parkTop: number,
  cellSize = SURFACE_CELL_SIZE,
  inset = PATH_FLOOR_TILE_INSET,
): THREE.Group {
  if (cells.length === 0) {
    throw new Error('createPathFloorPresentation requires at least one cell');
  }

  const group = new THREE.Group();
  const dummy = new THREE.Object3D();
  const fillSize = Math.max(cellSize - inset * 2, 0.2);
  const groutSize = cellSize + PATH_FLOOR_GROUT_OVERLAP;
  const byStyle = new Map<string, typeof cells>();
  for (const cell of cells) {
    const style = cell.style ?? 'classic';
    const list = byStyle.get(style) ?? [];
    list.push(cell);
    byStyle.set(style, list);
  }

  for (const [style, styled] of byStyle) {
    const pair = materialsForStyle(mats, style as PathFloorStyle);
    const groutGeometry = new THREE.BoxGeometry(groutSize, PATH_FLOOR_GROUT_HEIGHT, groutSize);
    groutGeometry.translate(0, PATH_FLOOR_GROUT_HEIGHT / 2, 0);
    const groutMesh = new THREE.InstancedMesh(groutGeometry, pair.edge, styled.length);
    const fillGeometry = new THREE.BoxGeometry(fillSize, PATH_FLOOR_FILL_HEIGHT, fillSize);
    fillGeometry.translate(0, PATH_FLOOR_FILL_HEIGHT / 2, 0);
    const fillMesh = new THREE.InstancedMesh(fillGeometry, pair.fill, styled.length);
    styled.forEach((cell, index) => {
      dummy.position.set(cell.x, cell.lift ?? PATH_FLOOR_SHARED_LIFT, cell.z);
      dummy.updateMatrix();
      groutMesh.setMatrixAt(index, dummy.matrix);
      fillMesh.setMatrixAt(index, dummy.matrix);
    });
    groutMesh.instanceMatrix.needsUpdate = true;
    fillMesh.instanceMatrix.needsUpdate = true;
    groutMesh.position.y = parkTop + PATH_FLOOR_GROUT_Y_OFFSET;
    fillMesh.position.y = pathFloorFillWorldY(parkTop);
    groutMesh.castShadow = false;
    groutMesh.receiveShadow = true;
    fillMesh.castShadow = false;
    fillMesh.receiveShadow = true;
    group.add(groutMesh, fillMesh);

    const bloomGeometry = mats.baked ? floralBloomGeometry(style as PathFloorStyle) : null;
    if (bloomGeometry && mats.baked) {
      const blooms = new THREE.InstancedMesh(bloomGeometry, mats.baked, styled.length);
      styled.forEach((cell, index) => {
        dummy.position.set(cell.x, (cell.lift ?? PATH_FLOOR_SHARED_LIFT) + PATH_FLOOR_FILL_HEIGHT, cell.z);
        dummy.rotation.y = ((cell.x * 7.3 + cell.z * 3.1) % 1) * Math.PI * 2;
        dummy.updateMatrix();
        blooms.setMatrixAt(index, dummy.matrix);
      });
      dummy.rotation.y = 0;
      blooms.instanceMatrix.needsUpdate = true;
      blooms.position.y = pathFloorFillWorldY(parkTop);
      blooms.castShadow = false;
      blooms.receiveShadow = false;
      blooms.userData.floralBlooms = style;
      group.add(blooms);
    }
  }

  group.userData.pathFloor = {
    cellCount: cells.length,
    cellSize,
    tileInset: inset,
    tileSize: fillSize,
    groutSize,
    bandWidthSubcells: 3,
    bandWorldWidth: 3 * cellSize,
    presentation: 'subcell-paving',
    styles: [...byStyle.keys()],
    overLawnLift: PATH_FLOOR_OVER_LAWN_LIFT,
    lawnVisualHeight: CONTEXTUAL_LAWN_VISUAL_HEIGHT,
  };
  group.userData.migration = { visual: 'pathFloor' };
  return group;
}

/** Logical 0.48 subcell minus a hairline so existing curb reads as grout. */
export const SUBGRID_LAWN_SIZE = 0.42;
export const SUBGRID_SLOT_CURB_SIZE = 2.4;
/** Continuous turf pad covering one legacy slot; 5x5 is the overlay, not 25 raised blocks. */
export const SUBGRID_CONTINUOUS_LAWN_SIZE = 2.4;
export const SUBGRID_GRID_LINE_WIDTH = 0.064;
export const SUBGRID_CUE_OPACITY = {
  normal: 0,
  build: 0.9,
} as const;

/** Edit-only overlay. Normal park view stays free of grid clutter. */
export const CANVAS_GRID_CUE_OPACITY = {
  normal: 0,
  build: 0.46,
} as const;

export type SubgridPresentation = 'raised-subcells' | 'contextual';
export type SubgridCueMode = keyof typeof SUBGRID_CUE_OPACITY;

function curbOverlayColor(mats: Pick<ParkMaterials, 'curb'>): number {
  const material = mats.curb;
  if ('color' in material && material.color instanceof THREE.Color) {
    return material.color.getHex();
  }
  return 0xece0c6;
}

function subgridLineGeometries(
  slotX: number,
  slotZ: number,
  slotSize: number,
  division = 5,
  lineWidth = SUBGRID_GRID_LINE_WIDTH,
): THREE.BufferGeometry[] {
  const half = slotSize / 2;
  const step = slotSize / division;
  const segments: THREE.BufferGeometry[] = [];
  for (let i = 0; i <= division; i += 1) {
    const offset = -half + i * step;
    const vertical = new THREE.BoxGeometry(lineWidth, 0.012, slotSize);
    vertical.translate(slotX + offset, 0, slotZ);
    segments.push(vertical);
    const horizontal = new THREE.BoxGeometry(slotSize, 0.012, lineWidth);
    horizontal.translate(slotX, 0, slotZ + offset);
    segments.push(horizontal);
  }
  return segments;
}

export function setSubgridCueMode(group: THREE.Group, mode: SubgridCueMode): void {
  const overlay = group.userData.subgridOverlay as THREE.Mesh | undefined;
  if (!overlay) {
    return;
  }
  const material = overlay.material;
  if (material instanceof THREE.MeshBasicMaterial) {
    const table = group.userData.canvasGrid ? CANVAS_GRID_CUE_OPACITY : SUBGRID_CUE_OPACITY;
    material.opacity = table[mode];
  }
  if (group.userData.subgridTerrain) {
    group.userData.subgridTerrain.cueMode = mode;
  }
}

/** Batched 5x5 lines for unlocked canvas cells. No raised lawn pads. */
export function createQuietCanvasGrid(
  mats: Pick<ParkMaterials, 'curb'>,
  cellCenters: Array<[number, number]>,
  parkTop: number,
  cueMode: SubgridCueMode = 'normal',
  slotSize = SUBGRID_SLOT_CURB_SIZE,
): THREE.Group {
  const group = new THREE.Group();
  if (cellCenters.length === 0) {
    group.userData.canvasGrid = { cellCount: 0, cueMode };
    return group;
  }
  const overlayGeos = cellCenters.flatMap(([x, z]) => subgridLineGeometries(x, z, slotSize));
  const overlayMaterial = new THREE.MeshBasicMaterial({
    color: curbOverlayColor(mats),
    transparent: true,
    opacity: CANVAS_GRID_CUE_OPACITY[cueMode],
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const overlay = new THREE.Mesh(mergeBufferGeometries(overlayGeos), overlayMaterial);
  overlay.position.y = parkTop + 0.15;
  overlay.renderOrder = 2;
  overlay.castShadow = false;
  overlay.receiveShadow = false;
  overlayGeos.forEach((geometry) => geometry.dispose());
  group.add(overlay);
  group.userData.subgridOverlay = overlay;
  group.userData.canvasGrid = { cellCount: cellCenters.length, cueMode, batched: true };
  group.userData.subgridTerrain = { cueMode, presentation: 'canvas-quiet' };
  return group;
}

/**
 * Edit-grid presentation from exact 0.48 subcell centers, not whole 2.4 legacy
 * blocks. Only the authoritative canvas cells receive a line, so the overlay
 * cannot spill outside the rounded island mask.
 */
export function createExactSubcellGrid(
  mats: Pick<ParkMaterials, 'curb'>,
  cellCenters: Array<[number, number]>,
  parkTop: number,
  cueMode: SubgridCueMode = 'normal',
  cellSize = SURFACE_CELL_SIZE,
): THREE.Group {
  const group = new THREE.Group();
  if (cellCenters.length === 0) {
    group.userData.canvasGrid = { cellCount: 0, cueMode, exact: true };
    return group;
  }
  const half = cellSize / 2;
  const lineWidth = 0.028;
  const overlayGeos = cellCenters.flatMap(([x, z]) => {
    const verticalLeft = new THREE.BoxGeometry(lineWidth, 0.012, cellSize);
    verticalLeft.translate(x - half, 0, z);
    const verticalRight = new THREE.BoxGeometry(lineWidth, 0.012, cellSize);
    verticalRight.translate(x + half, 0, z);
    const horizontalTop = new THREE.BoxGeometry(cellSize, 0.012, lineWidth);
    horizontalTop.translate(x, 0, z - half);
    const horizontalBottom = new THREE.BoxGeometry(cellSize, 0.012, lineWidth);
    horizontalBottom.translate(x, 0, z + half);
    return [verticalLeft, verticalRight, horizontalTop, horizontalBottom];
  });
  const overlayMaterial = new THREE.MeshBasicMaterial({
    color: curbOverlayColor(mats),
    transparent: true,
    opacity: CANVAS_GRID_CUE_OPACITY[cueMode],
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const overlay = new THREE.Mesh(mergeBufferGeometries(overlayGeos), overlayMaterial);
  overlay.position.y = parkTop + 0.15;
  overlay.renderOrder = 2;
  overlay.castShadow = false;
  overlay.receiveShadow = false;
  overlayGeos.forEach((geometry) => geometry.dispose());
  group.add(overlay);
  group.userData.subgridOverlay = overlay;
  group.userData.canvasGrid = { cellCount: cellCenters.length, cueMode, batched: true, exact: true };
  group.userData.subgridTerrain = { cueMode, presentation: 'exact-subcell' };
  return group;
}

/** Instanced 0.48 lawn tiles clipped to the unlocked canvas — never a 2.4 block past the island. */
export function createExactCanvasLawn(
  mats: Pick<ParkMaterials, 'lawnTile'>,
  cellCenters: Array<[number, number]>,
  parkTop: number,
  cellSize = SURFACE_CELL_SIZE,
): THREE.Group {
  const group = new THREE.Group();
  if (cellCenters.length === 0) {
    group.userData.exactLawn = { cellCount: 0 };
    return group;
  }
  const dummy = new THREE.Object3D();
  const lawnSize = Math.max(cellSize - 0.02, 0.2);
  const lawnGeometry = padGeometry(lawnSize, lawnSize, CONTEXTUAL_LAWN_PAD_HEIGHT, 0.04, CONTEXTUAL_LAWN_PAD_BEVEL);
  const lawnMesh = new THREE.InstancedMesh(lawnGeometry, mats.lawnTile, cellCenters.length);
  cellCenters.forEach(([x, z], index) => {
    dummy.position.set(x, 0, z);
    dummy.updateMatrix();
    lawnMesh.setMatrixAt(index, dummy.matrix);
  });
  lawnMesh.instanceMatrix.needsUpdate = true;
  lawnMesh.position.y = parkTop;
  lawnMesh.castShadow = false;
  lawnMesh.receiveShadow = true;
  group.add(lawnMesh);
  group.userData.exactLawn = { cellCount: cellCenters.length, cellSize: lawnSize };
  return group;
}

function createRaisedSubcellParcels(
  mats: Pick<ParkMaterials, 'curb' | 'lawnTile'>,
  slotCenters: Array<[number, number]>,
  subcellCenters: Array<[number, number]>,
  parkTop: number,
  lawnSize: number,
  slotSize: number,
): THREE.Group {
  const group = new THREE.Group();
  const dummy = new THREE.Object3D();

  const curbGeometry = padGeometry(slotSize, slotSize, 0.05, 0.12, 0.015);
  const curbMesh = new THREE.InstancedMesh(curbGeometry, mats.curb, slotCenters.length);
  slotCenters.forEach(([x, z], index) => {
    dummy.position.set(x, 0, z);
    dummy.updateMatrix();
    curbMesh.setMatrixAt(index, dummy.matrix);
  });
  curbMesh.instanceMatrix.needsUpdate = true;
  curbMesh.position.y = parkTop - 0.005;
  curbMesh.castShadow = false;
  curbMesh.receiveShadow = true;

  const lawnGeometry = padGeometry(lawnSize, lawnSize, 0.09, 0.06, 0.012);
  const lawnMesh = new THREE.InstancedMesh(lawnGeometry, mats.lawnTile, subcellCenters.length);
  subcellCenters.forEach(([x, z], index) => {
    dummy.position.set(x, 0, z);
    dummy.updateMatrix();
    lawnMesh.setMatrixAt(index, dummy.matrix);
  });
  lawnMesh.instanceMatrix.needsUpdate = true;
  lawnMesh.position.y = parkTop;
  lawnMesh.castShadow = false;
  lawnMesh.receiveShadow = true;

  group.add(curbMesh, lawnMesh);
  return group;
}

function createContextualParcels(
  mats: Pick<ParkMaterials, 'curb' | 'lawnTile'>,
  slotCenters: Array<[number, number]>,
  parkTop: number,
  slotSize: number,
  cueMode: SubgridCueMode,
): THREE.Group {
  const group = new THREE.Group();
  const dummy = new THREE.Object3D();

  const lawnGeometry = padGeometry(
    SUBGRID_CONTINUOUS_LAWN_SIZE,
    SUBGRID_CONTINUOUS_LAWN_SIZE,
    CONTEXTUAL_LAWN_PAD_HEIGHT,
    0.05,
    CONTEXTUAL_LAWN_PAD_BEVEL,
  );
  const lawnMesh = new THREE.InstancedMesh(lawnGeometry, mats.lawnTile, slotCenters.length);
  slotCenters.forEach(([x, z], index) => {
    dummy.position.set(x, 0, z);
    dummy.updateMatrix();
    lawnMesh.setMatrixAt(index, dummy.matrix);
  });
  lawnMesh.instanceMatrix.needsUpdate = true;
  lawnMesh.position.y = parkTop;
  lawnMesh.castShadow = false;
  lawnMesh.receiveShadow = true;

  const overlayGeos = slotCenters.flatMap(([x, z]) => subgridLineGeometries(x, z, slotSize));
  const overlayMaterial = new THREE.MeshBasicMaterial({
    color: curbOverlayColor(mats),
    transparent: true,
    opacity: SUBGRID_CUE_OPACITY[cueMode],
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const overlay = new THREE.Mesh(mergeBufferGeometries(overlayGeos), overlayMaterial);
  overlay.position.y = parkTop + 0.15;
  overlay.renderOrder = 2;
  overlay.castShadow = false;
  overlay.receiveShadow = false;
  overlayGeos.forEach((geometry) => geometry.dispose());

  group.add(lawnMesh, overlay);
  group.userData.subgridOverlay = overlay;
  return group;
}

/**
 * Unlocked buildable ground.
 * Default `raised-subcells` keeps the COL-003B 25-pad foundation for tests.
 * Production uses `contextual`: one continuous lawn per slot + one batched 5x5 overlay.
 */
export function createSubdividedBuildableParcels(
  mats: Pick<ParkMaterials, 'curb' | 'lawnTile'>,
  slotCenters: Array<[number, number]>,
  subcellCenters: Array<[number, number]>,
  parkTop: number,
  lawnSize = SUBGRID_LAWN_SIZE,
  slotSize = SUBGRID_SLOT_CURB_SIZE,
  presentation: SubgridPresentation = 'raised-subcells',
  cueMode: SubgridCueMode = 'normal',
): THREE.Group {
  if (slotCenters.length === 0 || subcellCenters.length === 0) {
    throw new Error('createSubdividedBuildableParcels requires slot and subcell centers');
  }

  const group =
    presentation === 'contextual'
      ? createContextualParcels(mats, slotCenters, parkTop, slotSize, cueMode)
      : createRaisedSubcellParcels(mats, slotCenters, subcellCenters, parkTop, lawnSize, slotSize);

  group.userData.subgridTerrain = {
    division: 5,
    subcellSize: 0.48,
    slotCount: slotCenters.length,
    subcellCount: subcellCenters.length,
    lawnSize: presentation === 'contextual' ? SUBGRID_CONTINUOUS_LAWN_SIZE : lawnSize,
    slotSize,
    presentation,
    cueMode: presentation === 'contextual' ? cueMode : 'normal',
  };
  return group;
}

/** Buildable parcels: raised mown-lawn tiles with a pale stone curb. */
export function createBuildableParcels(
  mats: ParkMaterials,
  spots: Array<[number, number]>,
  parkTop: number,
  size = 2.3,
): THREE.Group {
  const group = new THREE.Group();
  const curbs = spots.map(([x, z]) => {
    const geometry = padGeometry(size, size, 0.05, 0.36, 0.02);
    geometry.translate(x, 0, z);
    return geometry;
  });
  const lawns = spots.map(([x, z]) => {
    const geometry = padGeometry(size - 0.28, size - 0.28, 0.09, 0.28, 0.035);
    geometry.translate(x, 0, z);
    return geometry;
  });
  const curbMesh = new THREE.Mesh(mergeBufferGeometries(curbs), mats.curb);
  curbMesh.position.y = parkTop - 0.005;
  curbMesh.castShadow = false;
  curbMesh.receiveShadow = true;
  const lawnMesh = new THREE.Mesh(mergeBufferGeometries(lawns), mats.lawnTile);
  lawnMesh.position.y = parkTop;
  lawnMesh.castShadow = false;
  lawnMesh.receiveShadow = true;
  curbs.forEach((geometry) => geometry.dispose());
  lawns.forEach((geometry) => geometry.dispose());
  group.add(curbMesh, lawnMesh);
  return group;
}

/** Locked expansion land: quieter turf tile, low rope stakes and a small wooden "coming soon" sign. */
export function createLockedParcels(
  mats: ParkMaterials,
  spots: Array<[number, number]>,
  parkTop: number,
  size = 2.3,
): THREE.Group {
  const group = new THREE.Group();
  const tiles = spots.map(([x, z]) => {
    const geometry = padGeometry(size, size, 0.045, 0.34, 0.015);
    geometry.translate(x, 0, z);
    return geometry;
  });
  const tileMesh = new THREE.Mesh(mergeBufferGeometries(tiles), mats.grassQuiet);
  tileMesh.position.y = parkTop - 0.004;
  tileMesh.castShadow = false;
  tileMesh.receiveShadow = true;
  tiles.forEach((geometry) => geometry.dispose());
  group.add(tileMesh);

  const proto = new THREE.Group();
  const half = size / 2 - 0.12;
  const corners: Array<[number, number]> = [
    [-half, -half],
    [half, -half],
    [half, half],
    [-half, half],
  ];
  for (const [cx, cz] of corners) {
    proto.add(mesh(new THREE.CylinderGeometry(0.035, 0.045, 0.34, 6), mats.woodDark, { position: [cx, 0.17, cz] }));
    proto.add(mesh(new THREE.SphereGeometry(0.05, 6, 5), mats.woodLight, { position: [cx, 0.36, cz] }));
  }
  for (let i = 0; i < 4; i += 1) {
    const [ax, az] = corners[i]!;
    const [bx, bz] = corners[(i + 1) % 4]!;
    proto.add(rod(new THREE.Vector3(ax, 0.27, az), new THREE.Vector3(bx, 0.27, bz), 0.014, mats.rope));
  }
  proto.add(mesh(new THREE.BoxGeometry(0.06, 0.62, 0.06), mats.woodDark, { position: [0.45, 0.31, 0.62] }));
  proto.add(mesh(new THREE.BoxGeometry(0.62, 0.34, 0.05), mats.wood, { position: [0.45, 0.66, 0.62] }));
  proto.add(mesh(new THREE.BoxGeometry(0.5, 0.2, 0.02), mats.cream, { position: [0.45, 0.66, 0.65] }));

  const stakeGeometry = bakeColored(proto);
  const stakes = new THREE.InstancedMesh(stakeGeometry, mats.baked, spots.length);
  const dummy = new THREE.Object3D();
  spots.forEach(([x, z], index) => {
    dummy.position.set(x, parkTop + 0.04, z);
    dummy.updateMatrix();
    stakes.setMatrixAt(index, dummy.matrix);
  });
  stakes.instanceMatrix.needsUpdate = true;
  stakes.castShadow = true;
  stakes.receiveShadow = false;
  group.add(stakes);
  return group;
}
